"use client";

import { TestimonialTooltip } from "@/components/ui/TestimonialTooltip/TestimonialTooltip";
import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { BorderBeam } from "@/components/ui/border-beam";
import ProductShowcase from "@/components/Landing/ProductShowcase/ProductShowcase";

export default function HeroForMain(props) {
    const t = useTranslations();

    return (
        <div className="relative z-20 dark:bg-bgDark-400">
            {/* Grid background */}
            <div className="absolute inset-0 -z-20">
                <div
                    className="absolute inset-0 opacity-[0.4] dark:opacity-[0.15]"
                    style={{
                        backgroundImage: `linear-gradient(to right, #e5e7eb 1px, transparent 1px),
                                          linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)`,
                        backgroundSize: "64px 64px",
                    }}
                />
                {/* Radial fade */}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(circle at 50% 50%, transparent 0%, white 70%)",
                    }}
                />
            </div>
            {/* Decorative assets - hidden on mobile.
                aria-hidden sur le conteneur : ces quatre halos n'ont rien à
                annoncer, et « Asset » n'était le nom de rien. */}
            <div className="hidden lg:block" aria-hidden="true">
                <div
                    className="absolute w-[240px] aspect-[19/49] -z-10"
                    style={{ inset: "0% auto auto 0%" }}
                >
                    <Image
                        src="/images/site/assets/hero-main-top-left.png"
                        alt=""
                        fill
                    />
                </div>
                <div
                    className="absolute w-[380px] aspect-[190/141] -z-10"
                    style={{
                        inset: "auto auto 0% 0%",
                        WebkitMaskImage:
                            "linear-gradient(to top, transparent 0%, black 35%)",
                        maskImage:
                            "linear-gradient(to top, transparent 0%, black 35%)",
                    }}
                >
                    <Image
                        src="/images/site/assets/hero-main-bottom-left.png"
                        alt=""
                        fill
                    />
                </div>
                <div
                    className="absolute w-[391px] aspect-[391/267] -z-10"
                    style={{
                        inset: "auto 0% 0% auto",
                        WebkitMaskImage:
                            "linear-gradient(to top, transparent 0%, black 35%)",
                        maskImage:
                            "linear-gradient(to top, transparent 0%, black 35%)",
                    }}
                >
                    <Image
                        src="/images/site/assets/hero-main-bottom-right.png"
                        alt=""
                        fill
                    />
                </div>
                <div
                    className="absolute w-[244px] aspect-[488/1133] -z-10"
                    style={{ inset: "0% 0% auto auto" }}
                >
                    <Image
                        src="/images/site/assets/hero-main-top-right.png"
                        alt=""
                        fill
                    />
                </div>
            </div>
            <section className="max-w-6xl mx-auto md:py-32 py-14 px-6 relative z-20">
                <div className="text-center">
                    {/* Badge */}
                    <div
                        className="apparition mb-5"
                        style={{
                            "--apparition-duree": "0.5s",
                            "--apparition-delai": "0s",
                            "--apparition-y": "20px",
                        }}
                    >
                        <a
                            href="#vision"
                            className="relative inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white dark:bg-grayDark-900 border border-gray-200 dark:border-grayDark-700 text-sm text-gray-600 dark:text-grayDark-300 hover:bg-gray-50 dark:hover:bg-grayDark-800 transition-colors"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                            {t("HeroForMain_badge")}
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="m9 18 6-6-6-6" />
                            </svg>
                            <BorderBeam
                                duration={8}
                                size={100}
                                borderWidth={2}
                            />
                        </a>
                    </div>

                    {/* Headline */}
                    <h1
                        className="apparition text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white leading-tight"
                        style={{
                            "--apparition-duree": "0.5s",
                            "--apparition-delai": "0.1s",
                            "--apparition-y": "20px",
                        }}
                    >
                        {t("HeroForMain_title_line1")}
                        <br />
                        <span className="text-primary">
                            {t("HeroForMain_title_highlight")}
                        </span>
                    </h1>

                    {/* Subtitle */}
                    <p
                        className="apparition mt-6 text-lg sm:text-xl text-gray-600 dark:text-grayDark-400 mx-auto leading-relaxed max-w-[500px]"
                        style={{
                            "--apparition-duree": "0.5s",
                            "--apparition-delai": "0.2s",
                            "--apparition-y": "20px",
                        }}
                    >
                        {t("HeroForMain_headline")}
                    </p>

                    {/* CTAs */}
                    <div
                        className="apparition mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
                        style={{
                            "--apparition-duree": "0.5s",
                            "--apparition-delai": "0.3s",
                            "--apparition-y": "20px",
                        }}
                    >
                        <Link
                            href="/signup"
                            className="btn-landing btn-3d lip-orange w-full sm:w-auto px-8 py-3.5 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-full"
                        >
                            {t("HeroForMain_button")}
                        </Link>
                        <Link
                            href="/courses"
                            className="btn-3d w-full sm:w-auto inline-flex items-center justify-center px-8 py-3.5 bg-white dark:bg-grayDark-800 border border-gray-200 dark:border-grayDark-700 hover:border-gray-300 dark:hover:border-grayDark-600 hover:bg-gray-50 dark:hover:bg-grayDark-700 text-gray-700 dark:text-grayDark-200 text-sm font-semibold rounded-full transition-all duration-200"
                        >
                            {t("HeroForMain_secondary_cta")}
                        </Link>
                    </div>

                    {/* Réassurance : lève l'objection « on va me demander ma
                        carte » juste sous le bouton, là où elle se pose. Les
                        trois mentions sont vraies partout, sans condition de
                        pays, contrairement aux jours d'essai offerts. */}
                    <ul
                        className="apparition mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-gray-500 dark:text-grayDark-400"
                        style={{
                            "--apparition-duree": "0.5s",
                            "--apparition-delai": "0.35s",
                            "--apparition-y": "12px",
                        }}
                    >
                        {[
                            t("HeroForMain_reassurance_free"),
                            t("HeroForMain_reassurance_no_card"),
                            t("HeroForMain_reassurance_instant"),
                        ].map((mention) => (
                            <li
                                key={mention}
                                className="mb-0 inline-flex items-center gap-1.5"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                    className="shrink-0 text-primary"
                                >
                                    <path
                                        fill="currentColor"
                                        fillRule="evenodd"
                                        d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10s10-4.477 10-10S17.523 2 12 2m4.03 8.53a.75.75 0 0 0-1.06-1.06l-4.47 4.47l-1.47-1.47a.75.75 0 0 0-1.06 1.06l2 2a.75.75 0 0 0 1.06 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                                {mention}
                            </li>
                        ))}
                    </ul>


                    {/* Social Proof */}
                    <div
                        className="apparition mt-10 flex justify-center"
                        style={{
                            "--apparition-duree": "0.5s",
                            "--apparition-delai": "0.4s",
                            "--apparition-y": "20px",
                        }}
                    >
                        <TestimonialTooltip
                            center
                            nombre={props.nombreComptes}
                        />
                    </div>

                    {/* Démonstration du produit. La fenêtre est masquée aux
                        lecteurs d'écran à l'intérieur du composant ; ses
                        chapitres et son bouton pause, eux, restent
                        accessibles au clavier. */}
                    <div
                        className="apparition mt-16 relative z-30 md:mb-[-280px] mb-[-100px]"
                        style={{
                            "--apparition-duree": "0.7s",
                            "--apparition-delai": "0.5s",
                            "--apparition-y": "40px",
                        }}
                    >
                        <ProductShowcase />
                    </div>
                </div>
            </section>
        </div>
    );
}
